import { Component, inject, OnInit } from '@angular/core'
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms'
import { Store } from '@ngrx/store'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import {
  compareControlValidator,
  customEmailValidator,
  emailAndPasswordSimilarityValidation,
  handleServerError,
  validateAllFormFields,
} from 'models/form'
import { first, switchMap } from 'rxjs'
import { BaseModule } from 'shared/base/base.module'
import { CustomError } from 'src/app/constants/errors'
import { FeedbackService } from 'src/app/services/feedback.service'
import * as auth from '../../../actions/auth'
import * as fromRoot from '../../../reducers'
import { AuthService } from '../../../services/hubtype-api/auth.service'
import {
  SignUpData,
  UserService,
} from '../../../services/hubtype-api/user.service'

@Component({
  selector: 'signup-form',
  templateUrl: './signup-form.component.html',
  styleUrls: ['./signup-form.component.scss'],
  imports: [ReactiveFormsModule, BaseModule, TranslatePipe],
})
export class SignupFormComponent implements OnInit {
  private translate = inject(TranslateService)

  form = this.fb.group({
    email: ['', [Validators.required, customEmailValidator()]],
    password: [
      '',
      [
        Validators.required,
        Validators.minLength(12),
        Validators.maxLength(64),
        emailAndPasswordSimilarityValidation(),
      ],
    ],
    checkPassword: [
      '',
      [
        Validators.required,
        compareControlValidator(
          'password',
          this.translate.instant('auth.signup.form.passwordsDoNotMatch')
        ),
      ],
    ],
    organization: ['', [Validators.required, Validators.minLength(3)]],
    agree: [false, [Validators.requiredTrue]],
    updates: [false],
  })

  constructor(
    private store: Store<fromRoot.State>,
    private fb: UntypedFormBuilder,
    private feedbackService: FeedbackService,
    private userService: UserService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.cleanFormErrorsOnValueChange()
  }

  onSubmit() {
    validateAllFormFields(this.form)

    if (!this.form.valid) {
      return
    }

    this.signup()
  }

  private cleanFormErrorsOnValueChange() {
    this.form.valueChanges.subscribe(() => {
      this.form.setErrors(null)
    })
  }

  private getUserFromForm(): SignUpData {
    const { email, organization, password, updates } = this.form.value

    return {
      org_name: organization,
      email: email,
      password: password,
    }
  }

  private signup() {
    const signUpData = this.getUserFromForm()

    this.userService
      .signUpUser(signUpData)
      .pipe(
        first(),
        switchMap(() =>
          this.authService.login(signUpData.email, signUpData.password)
        )
      )
      .subscribe({
        next: oauth => {
          this.store.dispatch(new auth.LoginAction(oauth))
        },
        error: err => {
          handleServerError({
            error: err,
            form: this.form,
            feedbackService: this.feedbackService,
            customUnhandledError: {
              ...CustomError.SIGNUP_ERROR,
              message: this.translate.instant(
                CustomError.SIGNUP_ERROR.messageKey
              ),
            },
            lookup: { detail: 'password' },
          })
        },
      })
  }
}
